import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import RailIcon from '../../components/Layout/Sidebar/SidebarIcons';
import sidebarPrefsService from '../../services/sidebarPrefsService';
import { getRoleId } from '../../utils/roleUtils';
import {
  applySidebarOrder,
  getSidebarRailCatalog,
  isSidebarSettingsItem,
  moveSidebarItem,
  notifySidebarOrderChanged
} from '../../utils/sidebarOrder';
import './SidebarMenuSettings.css';

const SidebarMenuSettings = () => {
  const { user } = useSelector((state) => state.auth || {});
  const roleId = getRoleId(user);
  const catalog = useMemo(() => getSidebarRailCatalog(roleId), [roleId]);
  const [savedOrder, setSavedOrder] = useState([]);
  const [dragFromIndex, setDragFromIndex] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const dragFromIndexRef = useRef(null);
  const orderRef = useRef([]);

  const orderedItems = useMemo(
    () => applySidebarOrder(catalog, savedOrder, roleId),
    [catalog, savedOrder, roleId]
  );
  const reorderableItems = orderedItems.filter((item) => !isSidebarSettingsItem(item, roleId));
  const settingsItem = orderedItems.find((item) => isSidebarSettingsItem(item, roleId));
  orderRef.current = reorderableItems.map((item) => item.id);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await sidebarPrefsService.get();
        if (cancelled) return;
        const order = Array.isArray(response?.data?.order) ? response.data.order : [];
        setSavedOrder(order);
      } catch (err) {
        console.error('Failed to load sidebar order', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, roleId]);

  const persistOrder = async (nextOrder) => {
    const previous = savedOrder;
    setSavedOrder(nextOrder);
    notifySidebarOrderChanged(nextOrder);
    setIsSaving(true);
    try {
      const response = await sidebarPrefsService.save(nextOrder);
      const saved = Array.isArray(response?.data?.order) ? response.data.order : nextOrder;
      setSavedOrder(saved);
      notifySidebarOrderChanged(saved);
    } catch (err) {
      console.error('Failed to save sidebar order', err);
      setSavedOrder(previous);
      notifySidebarOrderChanged(previous);
      toast.error('Could not save menu order. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReorder = async (fromIndex, toIndex) => {
    const ids = reorderableItems.map((item) => item.id);
    const next = moveSidebarItem(ids, fromIndex, toIndex);
    if (next.join('|') === ids.join('|')) return;
    await persistOrder(next);
  };

  const handleReset = async () => {
    await persistOrder([]);
    toast.success('Sidebar menu restored to the default order.');
  };

  if (isLoading) {
    return (
      <section className="settings-section-card">
        <h3 className="settings-section-card-title">Sidebar Menu</h3>
        <p className="sidebar-menu-settings-help">Loading your menu order…</p>
      </section>
    );
  }

  return (
    <section className="settings-section-card">
      <div className="sidebar-menu-settings-header">
        <div>
          <h3 className="settings-section-card-title">Sidebar Menu</h3>
          <p className="sidebar-menu-settings-help">
            Drag items to change the order of the left sidebar. Settings always stays at the bottom.
          </p>
        </div>
        <button
          type="button"
          className="sidebar-menu-settings-reset"
          onClick={handleReset}
          disabled={isSaving}
        >
          Reset to default
        </button>
      </div>

      <div className="sidebar-menu-settings-list" role="list">
        {reorderableItems.map((item, index) => (
          <div
            key={item.id}
            role="listitem"
            className={`sidebar-menu-settings-row${dragFromIndex === index ? ' is-dragging' : ''}`}
            draggable={!isSaving}
            onDragStart={(event) => {
              dragFromIndexRef.current = index;
              setDragFromIndex(index);
              event.dataTransfer.effectAllowed = 'move';
              event.dataTransfer.setData('text/plain', String(index));
            }}
            onDragOver={(event) => {
              event.preventDefault();
              event.dataTransfer.dropEffect = 'move';
              const fromIndex = dragFromIndexRef.current;
              if (fromIndex == null || fromIndex === index) return;
              const ids = reorderableItems.map((row) => row.id);
              const next = moveSidebarItem(ids, fromIndex, index);
              if (next.join('|') === ids.join('|')) return;
              dragFromIndexRef.current = index;
              setDragFromIndex(index);
              orderRef.current = next;
              setSavedOrder(next);
              notifySidebarOrderChanged(next);
            }}
            onDrop={(event) => {
              event.preventDefault();
            }}
            onDragEnd={() => {
              persistOrder(orderRef.current);
              dragFromIndexRef.current = null;
              setDragFromIndex(null);
            }}
          >
            <span className="sidebar-menu-settings-grip" aria-hidden="true">⋮⋮</span>
            <span className="sidebar-menu-settings-icon">
              <RailIcon name={item.icon} />
            </span>
            <span className="sidebar-menu-settings-label">{item.label}</span>
            <span className="sidebar-menu-settings-index">{index + 1}</span>
            <div className="sidebar-menu-settings-actions">
              <button
                type="button"
                disabled={index === 0 || isSaving}
                onClick={() => handleReorder(index, index - 1)}
                aria-label={`Move ${item.label} up`}
              >
                ↑
              </button>
              <button
                type="button"
                disabled={index === reorderableItems.length - 1 || isSaving}
                onClick={() => handleReorder(index, index + 1)}
                aria-label={`Move ${item.label} down`}
              >
                ↓
              </button>
            </div>
          </div>
        ))}

        {settingsItem ? (
          <div className="sidebar-menu-settings-row is-pinned" role="listitem">
            <span className="sidebar-menu-settings-grip is-locked" aria-hidden="true">⌂</span>
            <span className="sidebar-menu-settings-icon">
              <RailIcon name={settingsItem.icon} />
            </span>
            <span className="sidebar-menu-settings-label">{settingsItem.label}</span>
            <span className="sidebar-menu-settings-pin">Pinned</span>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default SidebarMenuSettings;
